import { readonly, type Component } from 'vue';
import {
  createRouter as baseCreateRouter,
  createWebHistory,
  type RouteLocationNormalizedLoaded,
  type RouteLocationNormalized,
  type RouteLocationRaw,
  type RouteRecordRaw,
  type NavigationGuardNext,
  type Router,
} from 'vue-router';

import type { ServiceUniqueName } from '@bv/api/user';
import { useWatchRouterPermissions } from '@bv/composables';
import { MAINTENANCE_NOTICE_QUERY_PARAM } from '@bv/constants/maintenance/notice';
import { injectRoutes, integrationsRoutes, type ModuleLink, type RouteMeta } from '@bv/router';
import { authRoute } from '@bv/router/auth';
import { useUserStore } from '@bv/stores';
import { useAuthStore } from '@bv/stores/auth';
import { restoreFromMaintenance, settleMaintenanceNavigation } from '@bv/utils/maintenance/block';
import { evaluateRouteMetaAccessAsync, willRenderRoutePlaceholderState } from '@bv/utils/route/access';
import { getRedirectFrom, getRedirectPath } from '@bv/utils/route/query-string';
import { goToConsole } from '@bv/utils/url/console/go-to';

/**
 * Configuration for the application's sidebar navigation and actions.
 */
export interface Sidebar {
  /**
   * An array of navigation links/modules to display in the sidebar.
   */
  modules?: ModuleLink[];
  /**
   * A list of action components (e.g., buttons, quick actions) to render in the sidebar.
   */
  actions?: Component[];
  /**
   * Configuration for the user profile section within the sidebar.
   */
  user?: {
    /**
     * A component to display the user's current status (e.g., Online/Offline).
     */
    status?: Component;
    /**
     * A list of components to populate the user's dropdown menu.
     */
    menu?: Component[];
  };
}

type EvaluateRouteAccessDecision = { type: 'allow' } | { type: 'redirect'; to: RouteLocationRaw } | { type: 'reload' };

export type EvaluateRouteAccess = (route: RouteLocationNormalized) => Promise<EvaluateRouteAccessDecision>;

export type RedirectFrom = ServiceUniqueName | undefined;

/**
 * Creates a router instance with the given main route and sidebar.
 *
 * @param mainRoute - The main route to use for the router
 * @param sidebar - The sidebar to use for the router
 * @param layout - The layout to use for the router (`storybook` = Storybook dynamic shell)
 * @param injectAuthRoutes - When `true`, injects auth routes into mainRoute if not already present
 * @returns A router instance
 */
export const createRouter = ({
  mainRoute,
  sidebar = undefined,
  layout = 'old',
  injectAuthRoutes = false,
}: {
  mainRoute: RouteRecordRaw;
  sidebar?: Sidebar;
  layout?: 'old' | 'new' | 'storybook';
  injectAuthRoutes?: boolean;
}): Router => {
  if (injectAuthRoutes) {
    if (!mainRoute.children) {
      mainRoute.children = [];
    }

    // check if that the mainROute don't have an auth routes already
    const hasAuthRoute = mainRoute.children.some((child) => child.name === 'auth' || child.path === '/auth');
    if (!hasAuthRoute) {
      mainRoute.children.push(authRoute);
    }
  }

  const appRoutes: RouteRecordRaw[] = [injectRoutes(mainRoute, sidebar, layout), ...integrationsRoutes];

  const newRouter: Router = baseCreateRouter({
    history: createWebHistory(),
    routes: appRoutes,
  });

  // Create the route access evaluator for this router instance
  const evaluateRouteAccess = createEvaluateRouteAccess(newRouter);

  newRouter.beforeEach(
    async (
      to: RouteLocationNormalized,
      _from: RouteLocationNormalizedLoaded,
      next: NavigationGuardNext,
    ): Promise<void> => {
      const decision = await evaluateRouteAccess(to);

      if (decision.type === 'allow') {
        return next();
      }

      if (decision.type === 'reload') {
        restoreFromMaintenance();
        return next(false);
      }

      const resolvedTarget = newRouter.resolve(decision.to);

      // Avoid infinite loops by not redirecting to the same location
      if (to.fullPath === resolvedTarget.fullPath) {
        return next();
      }

      // Forward one-shot toast flags so they survive guard redirects
      const oneShotQueryKeys = ['showOtpWarning', MAINTENANCE_NOTICE_QUERY_PARAM] as const;
      const query = { ...resolvedTarget.query };
      let forwardedToastFlag = false;

      for (const key of oneShotQueryKeys) {
        if (to.query[key] && !query[key]) {
          query[key] = to.query[key];
          forwardedToastFlag = true;
        }
      }

      if (forwardedToastFlag) {
        return next({
          path: resolvedTarget.path,
          query,
          hash: resolvedTarget.hash,
        });
      }

      return next(decision.to);
    },
  );

  newRouter.onError((error, to) => {
    if (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.message.includes('Unable to preload CSS')
    ) {
      window.location.href = to.fullPath;
    }
  });

  // Set up reactive permission watching to ensure router guard
  // re-evaluates route access when permissions change
  useWatchRouterPermissions(newRouter, evaluateRouteAccess);

  setRouter(newRouter);

  return newRouter;
};

let routerInstance: Router | undefined;

export const setRouter = (router: Router): void => {
  routerInstance = router;
};

/**
 * Gets the router instance.
 *
 * @returns The router instance
 */
export const getRouter = (): Readonly<Router> | undefined => {
  return routerInstance ? (readonly(routerInstance) as unknown as Readonly<Router>) : undefined;
};

/**
 * Creates a route access evaluator function for a given router instance.
 * This shared function is used by both the global beforeEach guard and the reactive permission watcher.
 *
 * @param router - The Vue Router instance to use for route checks
 * @returns An evaluator function that returns:
 *   - { type: 'allow' }           → navigation can proceed
 *   - { type: 'redirect', to }    → should redirect to `to`
 *   - { type: 'reload' }          → leave the maintenance screen with a full page load
 *
 * It does **not** call `next()` or perform router.push; callers are
 * responsible for executing the navigation.
 */
export const createEvaluateRouteAccess = (router: Router): EvaluateRouteAccess => {
  const currentServiceUniqueName = import.meta.env.VITE_CURRENT_SERVICE_UNIQUE_NAME;

  return async (route: RouteLocationNormalized): Promise<EvaluateRouteAccessDecision> => {
    const maintenance = await settleMaintenanceNavigation(route);

    if (maintenance === 'reload') {
      return { type: 'reload' };
    }

    if (maintenance) {
      return { type: 'redirect', to: maintenance };
    }

    const meta = route.meta as RouteMeta;
    const { isPublic, isAuthPage, requiresAuth } = classifyRouteMeta(meta);

    // Public routes always allowed
    if (isPublic) {
      return { type: 'allow' };
    }

    const redirectFrom: RedirectFrom = getRedirectFrom(route.query?.redirectFrom);
    const redirectPath = getRedirectPath(route.query?.redirectPath);
    const { isAuthenticated } = await resolveAuthState();

    // Allow unauthenticated users to stay on auth pages (e.g., login)
    if (isAuthPage && !isAuthenticated) {
      return { type: 'allow' };
    }

    // Redirect unauthenticated users away from protected routes
    if (requiresAuth && !isAuthenticated) {
      const isForwardedFromOtherService = Boolean(redirectFrom && redirectFrom !== currentServiceUniqueName);
      const effectiveRedirectPath = isForwardedFromOtherService ? redirectPath : route.fullPath;

      const target = getAuthTarget(router, redirectFrom, effectiveRedirectPath);
      if (target) {
        return { type: 'redirect', to: target };
      }

      // Fallback to console redirect if no auth routes exist
      // Send the current service name and the full path the user tried to access,
      // so after login the user is redirected back to the same page.
      goToConsole(buildAuthRedirectQueryString(currentServiceUniqueName, route.fullPath));

      return { type: 'allow' };
    }

    // If already authenticated, redirect away from auth pages
    if (isAuthenticated && isAuthPage) {
      return { type: 'redirect', to: getHomeTarget(router) };
    }

    // Check permissions / meta-based accessibility
    if (await isAccessible(route)) {
      // Check if the route has a first accessible child redirect
      const firstAccessibleChildRedirect = await resolveFirstAccessibleChildRedirect(route, router);
      if (firstAccessibleChildRedirect) {
        return { type: 'redirect', to: firstAccessibleChildRedirect };
      }

      return { type: 'allow' };
    }

    // Redirect to unauthorized route if specified
    if (meta.redirectUnauthorized) {
      return { type: 'redirect', to: meta.redirectUnauthorized };
    }

    // Default home redirect
    return { type: 'redirect', to: getHomeTarget(router) };
  };
};

/**
 * Builds standard query parameter object for post-auth redirection.
 */
const buildAuthRedirectQueryParams = (redirectFrom?: RedirectFrom, redirectPath?: string): Record<string, string> => {
  const query: Record<string, string> = {};
  if (redirectFrom) {
    query.redirectFrom = redirectFrom;
  }
  if (redirectPath && redirectPath !== '/') {
    query.redirectPath = redirectPath;
  }
  return query;
};

/**
 * Builds formatted query string with leading question mark for console navigation fallback.
 */
const buildAuthRedirectQueryString = (redirectFrom?: RedirectFrom, redirectPath?: string): string | undefined => {
  const query = buildAuthRedirectQueryParams(redirectFrom, redirectPath);
  const keys = Object.keys(query);
  if (keys.length === 0) {
    return undefined;
  }
  return `?${new URLSearchParams(query).toString()}`;
};

/**
 * Determines the home route target for a given router instance.
 * Checks for 'home' route first, then 'main', otherwise falls back to '/'.
 *
 * @param router - The Vue Router instance to check for routes
 * @returns A RouteLocationRaw pointing to the home route
 */
export const getHomeTarget = (router: Router): RouteLocationRaw => {
  if (router.hasRoute('home')) {
    return { name: 'home' };
  } else if (router.hasRoute('main')) {
    return { name: 'main' };
  }
  return { path: '/' };
};

/**
 * Determines the authentication route target for a given router instance.
 * Checks for 'auth-login' route first, then 'auth', otherwise returns null.
 *
 * @param router - The Vue Router instance to check for routes
 * @param redirectFrom - Optional service name string to attach as query param
 * @param redirectPath - Optional path (within the redirectFrom service) to attach as query param
 * @returns A RouteLocationRaw pointing to the auth route, or null if no auth routes exist
 */
const getAuthTarget = (router: Router, redirectFrom?: RedirectFrom, redirectPath?: string): RouteLocationRaw | null => {
  const query = buildAuthRedirectQueryParams(redirectFrom, redirectPath);
  const hasQuery = Object.keys(query).length > 0;

  if (router.hasRoute('auth-login')) {
    return { name: 'auth-login', ...(hasQuery ? { query } : {}) };
  } else if (router.hasRoute('auth')) {
    return { name: 'auth', ...(hasQuery ? { query } : {}) };
  }
  return null;
};

const joinPath = (parent: string, child: string): string => {
  if (child.startsWith('/')) {
    return child;
  }

  const base = parent.endsWith('/') ? parent.slice(0, -1) : parent;
  const segment = child.startsWith('/') ? child : `/${child}`;
  return `${base}${segment}`;
};

/**
 * Resolves a redirect target for routes with children that cannot render themselves (no components).
 * Scans direct children in order and returns the first accessible child.
 */
const resolveFirstAccessibleChildRedirect = async (
  route: RouteLocationNormalized,
  router: Router,
): Promise<RouteLocationRaw | null> => {
  const record = route.matched[route.matched.length - 1];
  if (!record) {
    return null;
  }

  const hasComponent = !!record.components && Object.keys(record.components).length > 0;
  const hasChildren = (record.children?.length ?? 0) > 0;
  const redirectToFirstAccessibleChild = record.meta.redirectToFirstAccessibleChild;

  // Only auto-redirect parent routes that cannot render themselves
  if (!redirectToFirstAccessibleChild && (hasComponent || !hasChildren)) {
    return null;
  }

  for (const child of record.children ?? []) {
    const path = joinPath(record.path, child.path);
    const resolved = router.resolve({ path });

    // Skip if the child doesn't resolve to a real (named) child
    if (!resolved.name) {
      continue;
    }

    if (await isAccessible(resolved as RouteLocationNormalized)) {
      return { path };
    }
  }

  const meta = record.meta as RouteMeta;
  return meta.redirectUnauthorized ?? getHomeTarget(router);
};

/**
 * Checks if the route is accessible based on the route meta.
 *
 * @param route - The route to check
 * @returns A promise that resolves to true if the route is accessible, otherwise false
 */
const isAccessible = async (route: RouteLocationNormalized): Promise<boolean> => {
  const meta = route.meta as RouteMeta;
  // If the route has an upgradeState configured, owners are allowed through
  // the layout will render the upgrade state instead of the page component.
  if (meta.upgradeState && useUserStore().isOwner) {
    return true;
  }

  // Soft-allow when the layout will render empty state (honors onlyWhenInaccessible).
  if (await willRenderRoutePlaceholderState(meta)) {
    return true;
  }

  return evaluateRouteMetaAccessAsync(meta);
};

/**
 * Classifies the route meta into public, auth page, and requires authentication.
 *
 * @param meta - The route meta to classify
 * @returns An object with the classification
 */
const classifyRouteMeta = (
  meta: RouteMeta,
): {
  isPublic: boolean;
  isAuthPage: boolean;
  requiresAuth: boolean;
} => {
  const isPublic = !!meta.public;
  const isAuthPage = !!meta.guest;
  const requiresAuth = !!meta.authenticated || (!isPublic && !isAuthPage) || !!meta.permissions;

  return { isPublic, isAuthPage, requiresAuth };
};
/**
 * Resolves the authentication state of the user.
 * Checks for a token in the cookies and the user details in the user store.
 *
 * @returns A promise that resolves to an object with the authentication state
 */
const resolveAuthState = async (): Promise<{ isAuthenticated: boolean }> => {
  const authStore = useAuthStore();
  const hasToken: boolean = !!authStore.token || !!authStore.refreshToken || !!authStore.impersonateToken;

  if (!hasToken) {
    return { isAuthenticated: false };
  }

  const userStore = useUserStore();

  if (userStore.userDetails) {
    return { isAuthenticated: true };
  }

  const user = await userStore.getUserDetails();

  return {
    isAuthenticated: user != null,
  };
};
